import { motion, type Variants } from 'framer-motion';
import styled from 'styled-components';
import img1 from './images/fcb-profill.png';
import img2 from './images/enn530783.png';
import fjaraimg from './images/IMG_8386.png';
import skotheldimg from './images/skotheld.png';
import draugaimg from './images/draugamandarinur.jpg';
import { useIsMobile } from '../hooks/use-is-mobile';
import { colors, fonts } from '../styles/theme';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Hero = styled.div`
  width: 100%;
  height: 62vh;
  min-height: 380px;
  background-image: linear-gradient(
    rgba(42, 30, 21, 0.05),
    rgba(42, 30, 21, 0.55)
  ), url(${img1});
  background-size: cover;
  background-position: center 20%;
  display: flex;
  align-items: flex-end;
  padding: 28px 24px;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeroName = styled.h1`
  color: #FBF7EF;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.2;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
`;

const HeroRule = styled.div`
  width: 52px;
  height: 2px;
  background: ${colors.gold};
`;

const Section = styled.section`
  padding: 40px 24px;
`;

const BodyText = styled.div`
  font-size: 15.5px;
  line-height: 1.8;
  color: ${colors.ink};
  text-align: justify;

  p + p {
    margin-top: 16px;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 0 24px;

  img {
    width: 100%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    box-shadow: 0 12px 28px rgba(42, 30, 21, 0.18);
  }
`;

const Title = styled.h2`
  font-size: 28px;
  color: ${colors.accent};
`;

const GoldRule = styled.div`
  width: 48px;
  height: 2px;
  background: ${colors.gold};
  margin-bottom: 20px;
`;

const AwardCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-top: 3px solid ${colors.gold};
  padding: 24px 22px;
  box-shadow: 0 10px 24px rgba(42, 30, 21, 0.07);
  font-size: 14.5px;
  line-height: 1.75;
  text-align: justify;

  & + & {
    margin-top: 18px;
  }
`;

const AwardTitle = styled.p`
  font-family: ${fonts.display};
  font-weight: 600;
  font-size: 16.5px;
  color: ${colors.accent};
  margin-bottom: 8px;
  text-align: left;
  line-height: 1.35;

  @media (max-width: 420px) {
    font-size: 15px;
  }
`;

const JuryLabel = styled.p`
  font-size: 11.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.inkSoft};
  margin-bottom: 10px;
`;



export function MobileAbout() {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return null;
  }

  return (
    <MobileContainer>
      <Hero>
        <HeroText>
          <HeroName>Birgitta Björg Guðmarsdóttir</HeroName>
          <HeroRule />
        </HeroText>
      </Hero>

      <Section>
        <BodyText>
          <p>
            Birgitta Björg (f. 1998) er skáld og tónlistarkona, fædd og uppalin í Reykjavík.
            Hún gaf út sína fyrstu skáldsögu Skotheld (2018) aðeins tvítug að aldri en önnur skáldsaga hennar, Moldin heit (2024) hlaut Nýræktarstyrk Miðstöðvar íslenskra bókmennta og tilnefningu til Íslensku bókmenntaverðlaunanna og Fjöruverðlaunanna.
          </p>
          <p>
            Birgitta er stofnmeðlimur hljómsveitarinnar Ólafs Kram sem sigraði Músíktilraunir 2021 og hefur síðan gefið út tvær plötur, nefrennsli / kossaflens (2021) og Ekki treysta fiskunum (2022).
          </p>
          <p>
            Verk Birgittu hafa birst í Tímariti Máls og Menningar, Skírni og í Leirburði. Hún er meðlimur í listakollektívunni Heysahorn sem kom meðal annars fram á færeysku tónlistarhátíðunum Skrapt festival og G! festival sumarið 2023. Þá er Birgitta meðlimur í ljóðakollektívunni MÚKK.
          </p>
        </BodyText>
      </Section>

      <ImageGallery>
        <img src={img2} alt="Birgitta" />
        <img src={fjaraimg} alt="Birgitta 2" />
      </ImageGallery>

      <Section>
        <Title>Moldin heit</Title>
        <GoldRule />
        <BodyText>
          <p>
            Gefin út af Drápu bókaútgáfu.
            <br />
            Fáanleg í öllum helstu bókabúðum landsins.
          </p>
        </BodyText>
        <div style={{ height: '22px' }} />
        <AwardCard>
          <AwardTitle>Hlaut Fjöruverðlaunin 2025.</AwardTitle>
          <JuryLabel>Rökstuðningur dómnefndar:</JuryLabel>
          <p>
            Listir, náttúra og ólgandi tilfinningar takast á í bókinni Moldin heit eftir Birgittu Björgu Guðmarsdóttur. Hrynjandi ljóðræns textans knýr framvindu áfram þar sem meginstef eru ást, missir og innri leit. Form texta og litanotkun ljær frásögninni dýpt og er lesanda gefið rými til að skynja og túlka atburðarás sem lýkst upp eftir því sem líður á og rís hæst í sólódansi aðalpersónunnar. Birgitta Björg slær hér nýjan og forvitnilegan bókmenntatón.
          </p>
        </AwardCard>
        <AwardCard>
          <AwardTitle>Tilnefnd til Íslensku bókmenntaverðlaunanna 2025.</AwardTitle>
          <JuryLabel>Rökstuðningur dómnefndar:</JuryLabel>
          <p>
            Með léttum og hrífandi ritma er lesendum boðið upp í dans. Smám saman brýst út tilfinningaþrungin frásögn í listilega uppbyggðri skáldsögu. Listrænn en jafnframt aðgengilegur texti sem ætti að höfða til allra.
          </p>
        </AwardCard>
      </Section>

    </MobileContainer>
  );
}

const BookCard = styled(motion.div)`
  margin: 0 20px 26px;
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-top: 3px solid ${colors.gold};
  padding: 26px 22px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 14px 32px rgba(42, 30, 21, 0.08);
`;

const CoverFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 240px;
  margin: 0 auto;
  aspect-ratio: 2 / 3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 1;
    box-shadow: 0 16px 34px rgba(42, 30, 21, 0.18);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translate(9px, 9px);
    border: 1px solid ${colors.gold};
    z-index: 0;
  }
`;

const CardTitle = styled.h2`
  font-size: 26px;
  line-height: 1.2;
  color: ${colors.accent};
`;

const CardMeta = styled.p`
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.inkSoft};
`;

const AwardBadge = styled.p`
  align-self: flex-start;
  background: ${colors.cream};
  border: 1px solid ${colors.gold};
  color: ${colors.accent};
  font-family: ${fonts.display};
  font-weight: 600;
  font-size: 13.5px;
  line-height: 1.35;
  padding: 7px 13px;
`;

const CardBody = styled.div`
  font-size: 15.5px;
  line-height: 1.8;
  color: ${colors.ink};
  text-align: justify;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MobilePullQuote = styled.blockquote`
  margin: 0;
  padding: 4px 0 4px 16px;
  border-left: 2px solid ${colors.gold};
  font-family: ${fonts.display};
  font-style: italic;
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.accent};

  footer {
    margin-top: 8px;
    font-family: ${fonts.body};
    font-style: normal;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.inkSoft};
  }
`;

const listVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

export function MobileWorks() {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return null;
  }

  return (
    <MobileContainer>
      <Section>

      </Section>

      <motion.div variants={listVariants} initial="hidden" animate="show">
        <BookCard variants={cardVariants}>
          <CoverFrame>
            <img src={draugaimg} alt="Draugamandarínur" />
          </CoverFrame>
          <CardTitle>Draugamandarínur</CardTitle>
          <CardMeta>2025 · Ljóð</CardMeta>
          <AwardBadge>Hlaut bóksalaverðlaunin 2025</AwardBadge>
          <CardBody>
            <p>Gefin út af Drápu bókaútgáfu, fáanleg í helstu bókabúðum landsins.</p>
            <p>
              Hýðinu er flett af ávextinum. Fyrir innan leynist ýmislegt: matur, myrkur,
              minningar. Fyrir utan: vökult auga.
            </p>
            <p>
              Draugamandarínur fjallar um hvað það merkir að gefa af sér. Verkið skoðar,
              í gegnum hrylling, smáatriði og rytma, þá athöfn sem fer fram þegar matar
              er neytt. Líkaminn skipar mikilvægan sess í textanum, horft er inn á við
              eins og í gegnum röntgengeisla og þar má meðal annars sjá sársauka, hungur
              og aldinkjöt.
            </p>
          </CardBody>
          <MobilePullQuote>
            „Í Draugamandarínum beitir höfundur ljóðlistinni til þess að spretta
            tilverunni upp, stinga fingrinum inn og þreifa eftir kjarnanum. Hér er hlutum
            snúið á rönguna, innvolsið skafið út og holdið gegnumlýst svo beinin standa
            eftir ein og sér. (…) Ljóðin eru líkamleg, leikræn og safarík og rata beinustu
            leið undir skinnið.“
            <footer>- Snædís Björnsdóttir, Morgunblaðinu</footer>
          </MobilePullQuote>
        </BookCard>

        <BookCard variants={cardVariants}>
          <CoverFrame>
            <img src={img2} alt="Moldin heit" />
          </CoverFrame>
          <CardTitle>Moldin heit</CardTitle>
          <CardMeta>2024 · Skáldsaga</CardMeta>
          <AwardBadge>Hlaut Fjöruverðlaunin 2025</AwardBadge>
          <AwardBadge>Tilnefnd til Íslensku bókmenntaverðlaunanna 2025</AwardBadge>
          <CardBody>
            <p>Gefin út af Drápu bókaútgáfu, fáanleg í öllum helstu bókabúðum landsins.</p>
            <p>Moldin heit er skáldsaga um ástina, listina, sorg og missi.</p>
            <p>
              Ástin í lífi Karenar deyr. Hún mætir í jarðarförina óboðin, situr aftast og
              segir engum hver hún er. En eftir að hafa borið eina fortíð til grafa skýtur
              önnur upp kollinum. Hún sér Ými, kollega sinn, út um gluggann í
              erfidrykkjunni þar sem hann virðist ætla að fara sér að voða. Í kjölfarið er
              þeim hrundið af stað í ferð þar sem þau þurfa að horfast í augu við hvaða
              hlutverk þau leiki hvort í annars lífi og í flóknu dansverki, og hvaða nýju
              hlutverk standa þeim til boða.
            </p>
          </CardBody>
        </BookCard>

        <BookCard variants={cardVariants}>
          <CoverFrame>
            <img src={skotheldimg} alt="Skotheld" />
          </CoverFrame>
          <CardTitle>Skotheld</CardTitle>
          <CardMeta>2018 · Unglingssaga</CardMeta>
          <CardBody>
            <p>Skotheld segir frá ungu fólki í þorpi þar sem leyndarmál grúfa undir.</p>
            <p>
              Freyja vill komast hærra í metorðastiga þorpsins. Hún vingast við Agnesi
              sem hefur virðist hafa óflekkaða ímynd. Hægt og rólega sölsar Freyja undir
              sig stærri og stærri hlut af lífi Agnesar, starf hennar, kærasta og loks
              ímynd. En líf Agnesar er ekki dans á rósum, eins og Freyja kemst fljótt að.
            </p>
            <p>
              Grímur er sonur fyrrum hetju bæjarins og heyrir aldrei neitt slæmt sagt um
              föður sinn. Þar til einn daginn heyrir hann nokkuð misjafnt sem kemur honum
              úr jafnvægi og sendir hann í leit að sannleikanum um foreldra sína.
            </p>
          </CardBody>
        </BookCard>
      </motion.div>
    </MobileContainer>
  );
}
